import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Text, View, TouchableOpacity } from 'react-native';
import * as Crypto from 'expo-crypto';
import { useUnistyles } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import SelectInput from '@/components/General/inputs/SelectInput';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import Callout from '@/components/General/Callout';
import Checkbox from '@/components/General/inputs/Checkbox';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { useQuery } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { getFormPaymentOptions } from '@/data/formPayments';
import { deriveFieldId, ensureUniqueFieldId } from '@/utils/formsSchema';
import { SIGNER_ROLES, SIGNER_ROLE_BUILDER_LABELS, AGREEMENT_TEXT_MAX } from '@/utils/formSignaturePolicy';

/**
 * Options editor for a single schema field. Edits a local copy and calls
 * `onChange(updatedField)` on every change so the parent can persist into the
 * working schema. `allFieldIds` is the list of existing fieldIds (excluding
 * this field) for uniqueness validation.
 */
const FieldOptionsSheet = ({ isOpen, onClose, field, allFieldIds, onChange, formID }) => {
    const { theme: { colors } } = useUnistyles();
    const [local, setLocal] = useState(null);
    const [fieldIdError, setFieldIdError] = useState('');
    const fieldIdManuallyEdited = useRef(false);
    const { data: paymentOptions, error: paymentOptionsError } = useQuery({ queryKey: ['formPaymentOptions', formID || 'new'], queryFn: () => getFormPaymentOptions(formID), enabled: isOpen && field?.type === 'payment' });
    const [priceText, setPriceText] = useState('');
    const [optionAmountTexts, setOptionAmountTexts] = useState({});
    const lastFieldID = useRef(null);
    // Selected profile-view object — display state only; `config.viewID`
    // holds the persisted id.
    const [selectedProfileView, setSelectedProfileView] = useState(null);
    const profileViewQuery = Query.profileViews.byId({
        id: local?.config?.viewID,
        options: { enabled: isOpen && field?.type === 'profile' && !!local?.config?.viewID && !selectedProfileView },
    });

    useEffect(() => {
        if (field) {
            // Rehydrate only when a DIFFERENT field opens. The sheet's own
            // onChange writes flow back through this same `field` prop —
            // resetting on every update would clobber in-flight UI state
            // (the selected profile view, manual fieldId flag, price texts).
            if (lastFieldID.current === field.id) return;
            lastFieldID.current = field.id;
            setLocal(JSON.parse(JSON.stringify(field)));
            setSelectedProfileView(null);
            setPriceText(field.config?.amountCents != null ? (field.config.amountCents / 100).toFixed(2) : '');
            const texts = {};
            for (const opt of (Array.isArray(field.config?.amountOptions) ? field.config.amountOptions : [])) {
                texts[opt.id] = opt.amountCents != null ? (opt.amountCents / 100).toFixed(2) : '';
            }
            setOptionAmountTexts(texts);
            setFieldIdError('');
            fieldIdManuallyEdited.current = false;
        } else {
            lastFieldID.current = null;
            setLocal(null);
        }
    }, [field]);

    const update = (patch) => {
        if (!local) return;
        const next = { ...local, ...patch };
        setLocal(next);
        onChange(next);
    };

    const updateConfig = (configPatch) => {
        if (!local) return;
        const next = { ...local, config: { ...(local.config || {}), ...configPatch } };
        setLocal(next);
        onChange(next);
    };

    const handleLabelChange = (label) => {
        if (!local) return;
        // Auto-derive fieldId from label unless the user has manually edited it.
        const next = { ...local, label };
        if (!fieldIdManuallyEdited.current) {
            const derived = deriveFieldId(label);
            // Uniqueness is enforced against the real sibling ids — without it,
            // a second "Signature"/"Name" field would collide on fieldId and
            // clobber answers in the submitted payload.
            const stub = { sections: [{ fields: (allFieldIds || []).map((fieldId) => ({ fieldId })) }] };
            next.fieldId = ensureUniqueFieldId(derived, stub, local.fieldId);
        }
        setLocal(next);
        onChange(next);
    };

    const handleFieldIdChange = (raw) => {
        if (!local) return;
        fieldIdManuallyEdited.current = true;
        const candidate = String(raw).toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_|_$/g, '');
        if (candidate && allFieldIds.includes(candidate)) {
            setFieldIdError('This field ID is already used by another field.');
        } else {
            setFieldIdError('');
        }
        const next = { ...local, fieldId: candidate };
        setLocal(next);
        onChange(next);
    };

    // Hydrate the selected view object once the byId query resolves (e.g.
    // reopening options for a field that already has a view configured).
    useEffect(() => {
        if (profileViewQuery.data && !selectedProfileView) {
            setSelectedProfileView(profileViewQuery.data);
        }
    }, [profileViewQuery.data]);

    const type = local?.type;
    const isOptionsType = type === 'dropdown' || type === 'checkboxGroup';
    // Signature + profile fields have no input placeholder and the schema
    // trigger rejects default values — hide those controls entirely rather
    // than letting a save fail on a meaningless setting. Signatures also can
    // never be hidden (same trigger) — a hidden signature is unsigned evidence.
    const hidesPlaceholder = type === 'signature' || type === 'profile';
    const hidesHiddenToggle = type === 'signature';
    const hidesDefaultValue = type === 'signature' || type === 'profile';

    const renderDefaultValueEditor = () => {
        if (!['text', 'longtext', 'number', 'date', 'datetime', 'time', 'dropdown', 'checkbox', 'email', 'phone'].includes(type)) {
            return (
                <View style={styles.section}>
                    <Text style={styles.sectionLabel(colors)}>Default value not supported for {type} fields.</Text>
                </View>
            );
        }

        const val = local.defaultValue;
        switch (type) {
            case 'number': {
                const onUpdate = (text) => {
                    if (!text) {
                        update({ defaultValue: null });
                        return;
                    }
                    const num = Number(text);
                    update({ defaultValue: Number.isNaN(num) ? null : num });
                };
                return (
                    <Input
                        title="Default Value"
                        value={val != null ? String(val) : ''}
                        onUpdate={onUpdate}
                        type="decimal"
                        placeholder="Optional default number"
                    />
                );
            }
            case 'dropdown': {
                const options = (local.config?.options || []).map((opt) =>
                    typeof opt === 'object' && opt !== null ? opt : { label: String(opt), value: String(opt) }
                );
                if (options.length === 0) {
                    return (
                        <Input
                            title="Default Value"
                            value=""
                            disabled
                            placeholder="Add options above first"
                        />
                    );
                }
                return (
                    <SelectInput
                        title="Default Value"
                        placeholder="Select a default option"
                        value={val ?? null}
                        onUpdate={(selected) => update({ defaultValue: selected ?? null })}
                        options={options}
                    />
                );
            }
            case 'checkbox':
                return (
                    <ToggleRow
                        label="Default Value"
                        description="Should this field start checked?"
                        value={Boolean(val)}
                        onValueChange={(checked) => update({ defaultValue: checked })}
                    />
                );
            case 'date':
            case 'datetime':
            case 'time':
                return (
                    <DatePickerInput
                        title="Default Value"
                        mode={type === 'datetime' ? 'datetime' : type === 'time' ? 'time' : 'date'}
                        value={val ? new Date(val) : null}
                        onUpdate={(date) => update({ defaultValue: date ? date.toISOString() : null })}
                    />
                );
            case 'email':
                return (
                    <Input
                        title="Default Value"
                        value={val != null ? String(val) : ''}
                        onUpdate={(text) => update({ defaultValue: text || null })}
                        type="email"
                        placeholder="you@example.com"
                    />
                );
            case 'phone':
                return (
                    <Input
                        title="Default Value"
                        value={val != null ? String(val) : ''}
                        onUpdate={(text) => update({ defaultValue: text || null })}
                        type="tel"
                        placeholder="(555) 123-4567"
                    />
                );
            case 'longtext':
                return (
                    <Input
                        title="Default Value"
                        value={val != null ? String(val) : ''}
                        onUpdate={(text) => update({ defaultValue: text || null })}
                        placeholder="Optional default value"
                        multiline
                    />
                );
            case 'text':
            default:
                return (
                    <Input
                        title="Default Value"
                        value={val != null ? String(val) : ''}
                        onUpdate={(text) => update({ defaultValue: text || null })}
                        placeholder="Optional default value"
                    />
                );
        }
    };

    const optionsEditor = useMemo(() => {
        if (!isOptionsType || !local) return null;
        const options = local.config?.options || [];
        const setOptions = (newOptions) => updateConfig({ options: newOptions });

        return (
            <View style={styles.section}>
                <Text style={styles.sectionLabel(colors)}>Options</Text>
                {options.map((opt, i) => (
                    <View key={i} style={styles.optionRow}>
                        <Input
                            title={`Option ${i + 1} Label`}
                            value={opt.label}
                            onUpdate={(val) => {
                                const next = [...options];
                                next[i] = { ...next[i], label: val, value: val };
                                setOptions(next);
                            }}
                        />
                        <TouchableOpacity
                            onPress={() => setOptions(options.filter((_, idx) => idx !== i))}
                            style={styles.removeBtn}
                        >
                            <Text style={styles.removeText(colors)}>Remove</Text>
                        </TouchableOpacity>
                    </View>
                ))}
                <SolidButton
                    text={options.length === 0 ? 'Add an option' : 'Add another option'}
                    onPress={() => setOptions([...options, { label: '', value: '' }])}
                    variant="muted"
                    type="forms"
                    iconName="add-outline"
                    iconBefore
                    size="sm"
                />
            </View>
        );
    }, [isOptionsType, local, colors]);

    if (!local) return null;

    return (
        <SheetModal
            title="Field Options"
            isOpen={isOpen}
            onCancel={onClose}
            helpText="Configure how this field appears and behaves."
        >
            <View style={styles.content}>
                <Input
                    title="Label"
                    value={local.label}
                    onUpdate={handleLabelChange}
                    placeholder="e.g. Email Address"
                />

                <Input
                    title="Description"
                    value={local.description || ''}
                    onUpdate={(val) => update({ description: val })}
                    placeholder="Help text shown in a tooltip"
                />

                {!hidesPlaceholder && (
                    <Input
                        title="Placeholder"
                        value={local.placeholder || ''}
                        onUpdate={(val) => update({ placeholder: val })}
                        placeholder="you@example.com"
                    />
                )}

                <Input
                    title="Field ID"
                    value={local.fieldId}
                    onUpdate={handleFieldIdChange}
                    placeholder="auto-derived from label"
                    helpText="How conditions and integrations will read this field's data."
                />
                {fieldIdError ? (
                    <View style={styles.errorCallout}>
                        <Callout type="warning" message={fieldIdError} />
                    </View>
                ) : null}

                {/* Type-specific config */}
                {(type === 'text' || type === 'longtext') && (
                    <Input
                        title="Max Length"
                        value={local.config?.maxLength != null ? String(local.config.maxLength) : ''}
                        onUpdate={(val) => updateConfig({ maxLength: val ? Number(val) : undefined })}
                        placeholder="Optional character limit"
                        keyboardType="numeric"
                    />
                )}

                {type === 'payment' && (() => {
                    const amountOptions = Array.isArray(local.config?.amountOptions) ? local.config.amountOptions : null;
                    const multiAmount = amountOptions !== null;
                    const setAmountOptions = (next) => updateConfig({ amountOptions: next });
                    const updateOption = (index, patch) => {
                        const next = [...amountOptions];
                        next[index] = { ...next[index], ...patch };
                        setAmountOptions(next);
                    };
                    return (
                    <View style={styles.section}>
                        <ToggleRow
                            label="Offer multiple amounts"
                            description="Let people choose from a list of titled amounts."
                            value={multiAmount}
                            onValueChange={(on) => {
                                if (on) {
                                    const opt = { id: `opt_${Crypto.randomUUID()}`, label: '', amountCents: null };
                                    setAmountOptions([opt]);
                                    setOptionAmountTexts({ [opt.id]: '' });
                                } else {
                                    setAmountOptions(undefined);
                                    setOptionAmountTexts({});
                                }
                            }}
                            testID="payment-field-multiple-amounts"
                        />
                        {multiAmount ? (
                            <View>
                                <Text style={styles.sectionLabel(colors)}>Amounts</Text>
                                {amountOptions.map((opt, i) => (
                                    <View key={opt.id} style={styles.optionRow}>
                                        <Input
                                            title={`Option ${i + 1} Title`}
                                            value={opt.label}
                                            onUpdate={(val) => updateOption(i, { label: val })}
                                            placeholder="Bible Study Chart"
                                            testID={`payment-option-label-${i}`}
                                        />
                                        <Input
                                            title={`Option ${i + 1} Amount`}
                                            value={optionAmountTexts[opt.id] ?? ''}
                                            onUpdate={(text) => {
                                                setOptionAmountTexts((prev) => ({ ...prev, [opt.id]: text }));
                                                updateOption(i, { amountCents: /^\d+(\.\d{0,2})?$/.test(text) ? Math.round(Number(text) * 100) : null });
                                            }}
                                            keyboardType="decimal-pad"
                                            placeholder="50.00"
                                            testID={`payment-option-amount-${i}`}
                                        />
                                        <TouchableOpacity
                                            onPress={() => {
                                                setAmountOptions(amountOptions.filter((_, idx) => idx !== i));
                                                setOptionAmountTexts((prev) => {
                                                    const next = { ...prev };
                                                    delete next[opt.id];
                                                    return next;
                                                });
                                            }}
                                            style={styles.removeBtn}
                                            testID={`payment-option-remove-${i}`}
                                        >
                                            <Text style={styles.removeText(colors)}>Remove</Text>
                                        </TouchableOpacity>
                                    </View>
                                ))}
                                <SolidButton
                                    text={amountOptions.length === 0 ? 'Add an option' : 'Add another option'}
                                    onPress={() => {
                                        const opt = { id: `opt_${Crypto.randomUUID()}`, label: '', amountCents: null };
                                        setAmountOptions([...amountOptions, opt]);
                                        setOptionAmountTexts((prev) => ({ ...prev, [opt.id]: '' }));
                                    }}
                                    variant="muted"
                                    type="forms"
                                    iconName="add-outline"
                                    iconBefore
                                    size="sm"
                                    testID="payment-option-add"
                                />
                            </View>
                        ) : (
                            <Input title="Price" value={priceText} onUpdate={(text) => { setPriceText(text); updateConfig({ amountCents: /^\d+(\.\d{0,2})?$/.test(text) ? Math.round(Number(text) * 100) : null }); }} keyboardType="decimal-pad" placeholder="25.00" testID="payment-field-price" />
                        )}
                        <SelectInput title="Allowed Payment Methods" value={local.config?.allowedChannels || 'both'} onUpdate={(allowedChannels) => updateConfig({ allowedChannels })} hideClearButton options={[{ label: 'In Person Cash/Check', value: 'inPerson' }, { label: 'Electronic — Card or ACH', value: 'electronic' }, { label: 'Both', value: 'both' }]} />
                        <SelectInput title="Fund" value={local.config?.fundID || null} onUpdate={(fundID) => updateConfig({ fundID })} options={(paymentOptions?.funds || []).map((fund) => ({ value: fund.id, label: fund.name, subtitle: fund.isTaxDeductible ? 'Tax-deductible' : 'Non-tax-deductible' }))} required />
                        {paymentOptionsError && <Callout type="warning" message={paymentOptionsError.message} />}
                        <Callout type="info" message="Tax treatment comes from the fund. Fee coverage follows organization Giving settings. Changes apply only to future entries." />
                    </View>
                    );
                })()}

                {type === 'number' && (
                    <View style={styles.section}>
                        <Input
                            title="Min"
                            value={local.config?.min != null ? String(local.config.min) : ''}
                            onUpdate={(val) => updateConfig({ min: val ? Number(val) : undefined })}
                            placeholder="Optional"
                            keyboardType="numeric"
                        />
                        <Input
                            title="Max"
                            value={local.config?.max != null ? String(local.config.max) : ''}
                            onUpdate={(val) => updateConfig({ max: val ? Number(val) : undefined })}
                            placeholder="Optional"
                            keyboardType="numeric"
                        />
                        <Input
                            title="Step"
                            value={local.config?.step != null ? String(local.config.step) : ''}
                            onUpdate={(val) => updateConfig({ step: val ? Number(val) : undefined })}
                            placeholder="e.g. 1 or 0.01"
                            keyboardType="numeric"
                        />
                    </View>
                )}

                {(type === 'date' || type === 'datetime') && (
                    <View style={styles.section}>
                        <Input
                            title="Min Date"
                            value={local.config?.min || ''}
                            onUpdate={(val) => updateConfig({ min: val || undefined })}
                            placeholder="ISO date, e.g. 2024-01-01"
                        />
                        <Input
                            title="Max Date"
                            value={local.config?.max || ''}
                            onUpdate={(val) => updateConfig({ max: val || undefined })}
                            placeholder="ISO date, e.g. 2025-12-31"
                        />
                    </View>
                )}

                {isOptionsType && optionsEditor}

                {type === 'file' && (
                    <View style={styles.section}>
                        <ToggleRow
                            label="Allow Multiple Files"
                            description="Let users upload more than one file."
                            value={local.config?.allowMultiple ?? false}
                            onValueChange={(val) => updateConfig({ allowMultiple: val })}
                        />
                        <Input
                            title="Max Size (MB)"
                            value={local.config?.maxSizeMB != null ? String(local.config.maxSizeMB) : ''}
                            onUpdate={(val) => updateConfig({ maxSizeMB: val ? Number(val) : undefined })}
                            placeholder="10"
                            keyboardType="numeric"
                        />
                        <Input
                            title="Accepted File Types"
                            value={(local.config?.accept || []).join(', ')}
                            onUpdate={(val) => updateConfig({ accept: val ? val.split(',').map((s) => s.trim()).filter(Boolean) : [] })}
                            placeholder="image/*, application/pdf"
                        />
                    </View>
                )}

                {type === 'signature' && (() => {
                    const allowed = Array.isArray(local.config?.allowedSignerRoles)
                        ? local.config.allowedSignerRoles
                        : SIGNER_ROLES;
                    const toggleRole = (role) => {
                        const next = allowed.includes(role)
                            ? allowed.filter((r) => r !== role)
                            : [...SIGNER_ROLES.filter((r) => r === role || allowed.includes(r))];
                        // Keep at least one capacity — an empty list makes the
                        // field impossible to complete.
                        if (next.length === 0) return;
                        updateConfig({ allowedSignerRoles: next });
                    };
                    return (
                        <View style={styles.section}>
                            <Text style={styles.sectionLabel(colors)}>Who can sign</Text>
                            {SIGNER_ROLES.map((role) => (
                                <View key={role} style={styles.roleCheckRow}>
                                    <Checkbox
                                        checked={allowed.includes(role)}
                                        onChange={() => toggleRole(role)}
                                        disabled={allowed.includes(role) && allowed.length === 1}
                                        label={SIGNER_ROLE_BUILDER_LABELS[role]}
                                        testID={`signature-role-${role}`}
                                    />
                                </View>
                            ))}
                            <Input
                                title="Agreement Text"
                                value={local.config?.agreementText || ''}
                                onUpdate={(val) => updateConfig({ agreementText: val.slice(0, AGREEMENT_TEXT_MAX) })}
                                placeholder="Optional — shown above the signature pad"
                                multiline
                                testID="signature-agreement-text"
                            />
                            <Callout type="info" message="Signers always confirm an explicit consent statement before submitting — including on public forms." />
                        </View>
                    );
                })()}

                {type === 'profile' && (
                    <View style={styles.section}>
                        <SheetSelector
                            title="Profile View"
                            type="profileViews"
                            multiple={false}
                            placeholder="Optional — choose a saved view"
                            value={selectedProfileView}
                            onChange={(view) => {
                                setSelectedProfileView(view || null);
                                // Clearing the view must also clear the
                                // respondent-visibility opt-in — a view-less
                                // field can never show a card. Switching to a
                                // different view preserves the checkbox.
                                updateConfig(view?.id
                                    ? { viewID: view.id }
                                    : { viewID: null, showViewToRespondent: false });
                            }}
                            testID="profile-field-view-selector"
                        />
                        <ToggleRow
                            label="Show profile details to respondent"
                            description="After picking a person, show their profile view card while filling out the form."
                            value={local.config?.showViewToRespondent ?? false}
                            onValueChange={(val) => updateConfig({ showViewToRespondent: val })}
                            testID="profile-field-show-view"
                        />
                        <Callout type="info" message="Only people already in your People list can be selected. Profile fields can't be used on public forms." />
                    </View>
                )}

                {(type === 'name' || type === 'email' || type === 'phone' || type === 'address') && (
                    <View style={styles.section}>
                        {type === 'name' && (
                            <ToggleRow
                                label="Link to People"
                                description="Attempt to match submissions to existing Person records."
                                value={local.config?.linkToPeople ?? true}
                                onValueChange={(val) => updateConfig({ linkToPeople: val })}
                            />
                        )}
                        <ToggleRow
                            label="Autofill Logged-In User"
                            description="Pre-fill from the logged-in user's profile (in-app only)."
                            value={local.config?.autofillLoggedIn ?? true}
                            onValueChange={(val) => updateConfig({ autofillLoggedIn: val })}
                        />
                    </View>
                )}

                <View style={styles.section}>
                    <ToggleRow
                        label="Required"
                        description="Users must fill out this field to submit."
                        value={local.required ?? false}
                        onValueChange={(val) => update({ required: val })}
                    />
                    {!hidesHiddenToggle && (
                        <ToggleRow
                            label="Hidden"
                            description="Hide this field from the form (useful with default values)."
                            value={local.hidden ?? false}
                            onValueChange={(val) => update({ hidden: val })}
                        />
                    )}
                </View>

                {!hidesDefaultValue && renderDefaultValueEditor()}
            </View>
        </SheetModal>
    );
};

const styles = {
    content: { paddingBottom: 24 },
    section: { marginTop: 8 },
    sectionLabel: (colors) => ({
        fontSize: 11,
        fontFamily: 'heading',
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 6,
        marginLeft: 4,
    }),
    optionRow: { marginBottom: 4 },
    removeBtn: { flexDirection: 'row', alignItems: 'center', marginLeft: 12, marginTop: -4, marginBottom: 8 },
    removeText: (colors) => ({ color: colors.error, fontSize: 12 }),
    errorCallout: { marginBottom: 8 },
    roleCheckRow: { marginBottom: 8 },
};

export default FieldOptionsSheet;
